import React, { useContext, useEffect, useState } from 'react'
import AvatarSection from '../components/avatars/AvatarSection'
import CourseList from '../components/common/CourseList'
import AchievementList from '../components/common/AchievementList'
import TabMenu from '../components/common/TabMenu'
import CustomModal from '../components/common/CustomModal'
import useApi from '../hooks/useApi'
import { getMyCourses, getUser } from '../api/supabaseApi'
import { fetchAvatarMetadata } from '../api/readyPlayerApi'
import { AuthContext } from '../context/AuthContext'
import { ANIMATIONS } from '../constants/animations'
import confetti from 'canvas-confetti'
import ProfileSection from '../components/common/ProfileSection'
import { getIdFromUrl } from '../utils/helpers'
import {useLocation, useNavigate} from 'react-router-dom'
import PrimaryButton from "../components/buttons/PrimaryButton";
import LoadingOverlay from "../components/common/LoadingOverlay";
import UpdateProfile from '../components/common/UpdateProfile'

const Home = () => {
  const {
    session: { user },
  } = useContext(AuthContext)
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const tab = queryParams.get('tab')
  const navigate = useNavigate()
  const [avatarUrl, setAvatarUrl] = useState(null)
  const [animationUrl, setAnimationUrl] = useState(null)
  const [currentTab, setCurrentTab] = useState(tab || 'course_library')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [badgeModal, setBadgeModal] = useState('')
  const [reloadProfile, setReloadProfile] = useState(true)

  const { data: profile, loading: profileLoading } = useApi(
    () => getUser(user.id),
    [reloadProfile]
  )
  const { data: courseData, loading: courseLoading } = useApi(
    () => getMyCourses(profile?.id, 'main'),
    [profile]
  )
  const { data: miniCourseData, loading: miniCourseLoading } = useApi(
    () => getMyCourses(profile?.id, 'mini'),
    [profile]
  )
  const { data: avatarMeta } = useApi(
    () => fetchAvatarMetadata(getIdFromUrl(avatarUrl)),
    [avatarUrl]
  )

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search)
    queryParams.set('tab', currentTab)
    navigate(`?${queryParams.toString()}`, { replace: true })
  }, [location.search, navigate, currentTab])

  // Update avatarUrl on profile load
  useEffect(() => {
    if (!profileLoading) {

      if (!profile.real_email) navigate('/setup-email')

      if (profile?.avatars) {
        setAvatarUrl(profile.avatars.readyplayer_url)
      } else {
        navigate('/create-avatar')
      }
    }
  }, [profile, profileLoading])

  // Set animation URL based on avatar metadata
  useEffect(() => {
    if (avatarMeta) {
      const gender = avatarMeta.outfitGender
        ? avatarMeta.outfitGender.toUpperCase()
        : null
      setAnimationUrl(gender ? ANIMATIONS[gender].IDLE : null)
    }
  }, [avatarMeta])

  const openModal = (badge) => {
    setBadgeModal(badge)
    setIsModalOpen(true)
    confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } })
  }

  const closeModal = () => setIsModalOpen(false)

  if (profileLoading) return <LoadingOverlay />

  return (
    <div className="md:pb-4 w-full mx-auto ">
      <div className="flex md:flex-row flex-col md:gap-4">
        <div className="md:w-[40%] md:rounded-lg md:bg-[#FFF8ED] md:p-4 md:h-[761px]">
          <AvatarSection avatarUrl={avatarUrl} animationUrl={animationUrl} />
        </div>
        <div className="md:w-[60%] rounded-lg bg-[#FFF8ED] p-4 max-h-[761px] md:overflow-scroll">
          <div className="my-4">
            <ProfileSection avatarUrl={avatarUrl} profile={profile} setReloadProfile={setReloadProfile} />
          </div>
          <TabMenu currentTab={currentTab} setCurrentTab={setCurrentTab} />
          <div className="mt-4">
            <div className="text-sm px-4">
              If it is your first time to enroll on our courses, please make sure to enter <span className="font-bold">the same email address you used for your OC-ID</span>.
            </div>
            {currentTab === 'course_library' ? (
              <CourseList
                mainCourses={courseData}
                miniCourses={miniCourseData}
                loading={courseLoading || miniCourseLoading}
              />
            ) : (
              <AchievementList
                courses={courseData ? courseData.concat(miniCourseData) : []}
                openModal={openModal}
              />
            )}
          </div>
        </div>
      </div>
      <CustomModal isOpen={isModalOpen} onClose={closeModal}>
        <div className="flex flex-col items-center gap-4">
          <img
            className="h-52 w-auto"
            src={badgeModal.image_url}
            alt="achievement"
          />
          <p className="text-3xl font-bold text-primary">{badgeModal.name}</p>
          <p className="text-base text-black">{badgeModal.description}</p>
          <PrimaryButton className="w-48" onClick={closeModal}>Close</PrimaryButton>
        </div>
      </CustomModal>
    </div>
  )
}

export default Home
